import React, { useState, useEffect, useMemo } from "react";
import {
  Header,
  Button,
  Icon,
  Container,
  Placeholder,
  Pagination,
} from "semantic-ui-react";
import _ from "lodash";
import { configOptions } from "env.config";
import { HostFormModal } from "components/Admin/DeveloperInfo/HostFormModal";
import { SearchBar } from "../SearchBar";
import { limitConcurrentRequests } from "utils/function-utils";
import {
  addAppHost,
  deleteAppHost,
  getMES16ApiColor,
  getMES16AppHostList,
  getMES16HostList,
  updateAppHost,
} from "services/developer";
import { HOST_COLUMN_LIST, REQUEST_LIMIT, PAGE_SIZE } from "env.config";
import { store } from "services/state";
import { updateSubscriptions } from "services/api-catalog";

const HostActionButton = ({ appDetail, onOpenAddModal }) => {
  if (!appDetail || !appDetail.AppId) {
    return null;
  }

  return (
    <div className="action-group">
      <Button icon onClick={onOpenAddModal}>
        <Icon name="plus" />
      </Button>
    </div>
  );
};

const HostDetail = ({ ENVIDE, apiKey, appDetail, profileList, handleMessage, editTagRef }) => {
  const [appHostList, setAppHostList] = useState([]);
  const [hostList, setHostList] = useState([]);
  const [renderList, setRenderList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [editStatus, setEditStatus] = useState(false);
  const [formData, setFormData] = useState({});
  const [filter, setFilter] = useState({ column: "Host", value: "" });
  useEffect(() => {
    (async () => {
      if (appDetail) {
        if (appDetail.AppId) {
          if (editTagRef.current) return;
          const hostList = await getHostList();
          const totalPages = Math.ceil(hostList.length / PAGE_SIZE);
          setTotalPages(totalPages);
          setRenderList(hostList.slice(0, PAGE_SIZE));
        } else {
          setRenderList([]);
          setTotalPages(0);
        }
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDetail]);

  useEffect(() => {
    const renderList = appHostList.slice(
      activePage * PAGE_SIZE,
      (activePage + 1) * PAGE_SIZE
    );
    setRenderList(renderList);
  }, [activePage, appHostList]);

  const onChangeFilter = (_e, data) => {
    const column = data.selection ? data.value : filter.column;
    const value = data.selection ? filter.value : data.value;
    let availHostList = appHostList;
    if (column && value) {
      availHostList = appHostList.filter(
        (item) => item[column].toLowerCase().indexOf(value.toLowerCase().trim()) > -1
      );
    }
    const totalPages = Math.ceil(availHostList.length / PAGE_SIZE);
    setRenderList(availHostList.slice(0, PAGE_SIZE));
    setTotalPages(totalPages);
    setActivePage(0);
    setFilter({ column, value });
  };

  const profileOptions = useMemo(() => {
    const { host } = formData;
    const filteredHost = hostList.filter((item) => item.Host === host);
    if (!filteredHost[0]?.Host) {
      return [];
    }
    return profileList
      .filter((profile) => profile.Host === filteredHost[0]?.Host)
      .map((profile) => {
        return {
          key: profile.ProfileId,
          text: profile.ProfileName,
          value: profile.ProfileId,
          scope: profile.Scope,
          stage: profile.Stage,
          mes: profile.MES,
          host: profile.Host,
        };
      });
  }, [profileList, hostList, formData]);

  const hostOptions = useMemo(() => {
    return hostList
      .sort((a, b) => {
        if (a.Host !== b.Host) {
          return a.Host.localeCompare(b.Host);
        }
        return a; 
      })
      .map((host) => {
        return {
          key: host.Host,
          text: host.Host,
          value: host.Host,
        };
      });
  }, [hostList]);

  const getHostList = async () => {
    const [appHostList, hostList] = await Promise.all([
      getMES16AppHostList({ UserName: appDetail.UserName }),
      getMES16HostList(),
    ]);
    const urls = appHostList.map((item) => item.Host);
    const res = await limitConcurrentRequests(
      (url) => getMES16ApiColor(url),
      urls,
      REQUEST_LIMIT
    );
    appHostList.forEach((host) => {
      host.Version = res[host.Host]?.version;
    });
    setHostList(hostList);
    setAppHostList(appHostList);
    return appHostList;
  };

  const onChangeFormItem = (formKey, value) => {
    const formKeyValid = formKey + "Valid";
    if (formKey === "host") {
      const hostDetail = hostList.find((item) => item.Host === value);
      setFormData({
        ...formData,
        host: value,
        hostValid: true,
        scope: hostDetail.Scope,
        stage: hostDetail.Stage,
        // change host and reset profile
        profile: "",
      });
      return;
    }
    setFormData({ ...formData, [formKey]: value, [formKeyValid]: true });
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  const onValidateFormModal = () => {
    const { host, target, profile } = formData;
    if (!host || !target || !profile) {
      setFormData({
        ...formData,
        hostValid: host,
        targetValid: target,
        profileValid: profile,
      });
      return;
    }
    return true;
  };

  const onSaveHost = async () => {
    if (!onValidateFormModal()) return;
    setOpenFormModal(false);
    setLoaded(false);
    if (editStatus) {
      onUpdateAppHost();
    }
    if (!editStatus) {
      onAddAppHost();
    }
  };

  const onUpdateAppHost = async () => {
    const { host, scope, stage, target, profile } = formData;
    const params = {
      UserName: appDetail.UserName,
      Host: host,
      Scope: scope,
      Stage: stage,
      Target: target,
      Profile: profile,
    };
    const res = await updateAppHost(params);
    if (res.success) {
      const hostList = await getHostList();
      const totalPages = Math.ceil(hostList.length / PAGE_SIZE);
      setFilter({ column: "Host", value: "" });
      setTotalPages(totalPages);
      setRenderList(hostList.slice(0, PAGE_SIZE));
    }
    handleMessage({
      success: res.success,
      operation: "updateAppHost",
      content: res.message,
    });
    setLoaded(true);
  };

  const getUsagePlanIdParams = () => {
    // only apiGateway api need to subscribe
    const apiList = store.apiList.apiGateway;
    const acceptableEnvideList = new Set();
    // app assigned scopes
    const scopeList = appDetail.OauthScope.map((scope) =>
      scope.replace(/^42QAPI\//, "")
    );
    // scopes map to envide
    for (let key in ENVIDE) {
      if (scopeList.includes(ENVIDE[key].scope)) {
        acceptableEnvideList.add(key);
      }
    }
     // envide map to related apis
     const acceptableApiList = apiList.filter((api) => {
      let apiEnv = "";
      const swaggerApiName = api.swaggerApiName;
      if (new RegExp(`^MES16-.*`).test(swaggerApiName)) {
        apiEnv = swaggerApiName.split("-")[1];
      } else {
        apiEnv = swaggerApiName.split("-")[0];
      }
      return acceptableEnvideList.has(apiEnv);
    });
    // api map to usagePlanId
    const usagePlanIdList = Object.keys(
      _.groupBy(acceptableApiList, (api) => {
        return api.usagePlan.id;
      })
    );
    return usagePlanIdList;
  };

  const onAddAppHost = async () => {
    const { host, scope, stage, target, profile } = formData;
    const addUsagePlanId = getUsagePlanIdParams();
    const params = {
      UserName: appDetail.UserName,
      Host: host,
      Scope: scope,
      Stage: stage,
      Target: target,
      Profile: profile,
      AddUsagePlanId: addUsagePlanId,
      ApiKey: apiKey
    };
    const res = await addAppHost(params);
    if (res.success) {
      const hostList = await getHostList();
      await updateSubscriptions(true).catch((err) => Promise.resolve(err));
      const totalPages = Math.ceil(hostList.length / PAGE_SIZE);
      setFilter({ column: "Host", value: "" });
      setTotalPages(totalPages);
      setRenderList(hostList.slice(0, PAGE_SIZE));
    }
    handleMessage({
      success: res.success,
      operation: "addAppHost",
      content: res.message,
    });
    setLoaded(true);
  };

  const onDeleteAppHost = async (host) => {
    setLoaded(false);
    const params = { UserName: appDetail.UserName, Host: host };
    const res = await deleteAppHost(params);
    if (res.success) {
      const hostList = await getHostList();
      const totalPages = Math.ceil(hostList.length / PAGE_SIZE);
      setFilter({ column: "Host", value: "" });
      setTotalPages(totalPages);
      setRenderList(hostList.slice(0, PAGE_SIZE));
    }
    handleMessage({
      success: res.success,
      operation: "deleteAppHost",
      content: res.message,
    });
    setLoaded(true);
  };

  const initFormData = (editStatus, selectedItem) => {
    if (editStatus) {
      setFormData({
        host: selectedItem.Host,
        hostValid: true,
        profile: selectedItem.Profile,
        profileValid: true,
        target: selectedItem.Target,
        targetValid: true,
        scope: selectedItem.Scope,
        stage: selectedItem.Stage,
      });
    } else {
      setFormData({
        host: "",
        hostValid: true,
        profile: "",
        profileValid: true,
        target: "",
        targetValid: true,
      });
    }
  };

  const onOpenAddModal = () => {
    initFormData(false);
    setEditStatus(false);
    setOpenFormModal(true);
  };

  const onOpenEditModal = (host) => {
    initFormData(true, host);
    setEditStatus(true);
    setOpenFormModal(true);
  };

  return (
    <Container
      style={{ position: "relative", marginBottom: "20px", width: "100%" }}
      className="inner-container host-detail"
    >
      <Header as="h3">Host Detail</Header>
      <div className="search-container">
        <SearchBar
          filter={filter}
          onChangeFilter={onChangeFilter}
          options={configOptions.appHostFilter}
        />
      </div>
      <HostActionButton appDetail={appDetail} onOpenAddModal={onOpenAddModal} />
      <div>
        <HostDetailItem
          onDeleteAppHost={onDeleteAppHost}
          onOpenEditModal={onOpenEditModal}
          renderList={renderList}
          loaded={loaded}
        />
        <HostFormModal
          editStatus={editStatus}
          openFormModal={openFormModal}
          formData={formData}
          onChangeFormItem={onChangeFormItem}
          onCloseFormModal={onCloseFormModal}
          onSaveHost={onSaveHost}
          profileOptions={profileOptions}
          hostOptions={hostOptions}
        />
      </div>
      <Container fluid textAlign="right">
        <Pagination
          activePage={activePage + 1}
          onPageChange={(_e, { activePage }) => setActivePage(activePage - 1)}
          totalPages={totalPages}
        />
      </Container>
    </Container>
  );
};

const HostDetailItem = ({
  onDeleteAppHost,
  onOpenEditModal,
  renderList,
  loaded,
}) => {
  const PendingHostList = () => {
    const DEFAULT_LENGTH = 10;
    const DEFAULT_SIZE1 = 2;
    const DEFAULT_SIZE2 = 6;

    return (
      <div>
        {Array.from({ length: DEFAULT_LENGTH }).map((_value, index) => {
          return (
            <div className="host-item-container" key={index}>
              <div className="host-item-content">
                {Array.from({ length: DEFAULT_SIZE1 }).map((item, index) => (
                  <div
                    className="host-item"
                    style={{ padding: "8px" }}
                    key={index}
                  >
                    <Placeholder
                      style={{ width: "100%", maxWidth: "none" }}
                      key={item}
                    />
                  </div>
                ))}
              </div>
              <div className="host-item-content">
                {Array.from({ length: DEFAULT_SIZE2 }).map((_value, index) => (
                  <div
                    className="host-item"
                    style={{ padding: "8px" }}
                    key={index}
                  >
                    <Placeholder style={{ width: "100%", maxWidth: "none" }} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!loaded) {
    return <PendingHostList />;
  }
  return (
    <div>
      {renderList.map((host) => {
        return (
          <div
            className="host-item-container"
            key={`${host.Host}${host.Envide}`}
          >
            <div className="host-item-content">
              <div className="host-item" style={{ width: "auto" }}>
                <b>Host</b>
                <div>
                  {host.Host} ({host.Name})
                </div>
              </div>
              {/* <div className="host-item">
                <b>Alias</b>
                <div>{host.Name}</div>
              </div> */}
              <div className="host-action-group">
                <Button icon onClick={() => onDeleteAppHost(host)}>
                  <Icon name="trash" />
                </Button>
                <Button icon onClick={() => onOpenEditModal(host)}>
                  <Icon name="pencil" />
                </Button>
              </div>
            </div>

            <div className="host-item-content">
              {HOST_COLUMN_LIST.map((item) => {
                return (
                  <div key={item.key} className="host-item">
                    <b>{item.label}</b>
                    <div>{host[item.key]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { HostDetail };
